import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Typography,
  InputLabel,
  CircularProgress,
  LinearProgress,
  Backdrop,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { validateRefCode } from "../utils/api";
import "react-toastify/dist/ReactToastify.css";
import {
  clickNext,
  uploadVideo,
  submitForm,
  fetchDataByEmail,
} from "../utils/api";
import { validateVideoFile } from "../utils/videoValidation";
import ReCaptcha from "../components/ReCaptcha";

const RegisterPage = () => {
  const { referralParam } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState(["", "", "", "", "", ""]);
  const [isReferralLocked, setIsReferralLocked] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedVideoFilename, setUploadedVideoFilename] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const [isEmailValidating, setIsEmailValidating] = useState(false);
  const [isEmailLocked, setIsEmailLocked] = useState(false);
  const emailAbortControllerRef = useRef(null);
  const [formData, setFormData] = useState({
    // Step 1
    name: "",
    age: "",
    gender: "",
    district: "",
    assemblyConstituency: "",
    panchayat: "",
    village: "",
    phone: "",
    email: "",
    himachaliDomicile: "",
    category: "",
    bpl: "",
    maritalStatus: "",
    referralCode: "",
    // Step 2
    educationalQualification: "",
    profession: "",
    youthOrganization: "",
    leadershipProgram: "",
    motherName: "",
    motherProfession: "",
    fatherName: "",
    fatherProfession: "",
    siblings: "",
    // Step 3
    mountainOrBeach: "",
    favoritePodcaster: "",
    favoritePodcasterOther: "",
    binodOrKiris: "",
    aiPreference: "",
    noobFriends: "",
    aiImagePrompt: "",
    ghazalOrHipHop: "",
    changemakersWord: "",
    issueToSolve: "",
    videoUpload: null,
  });

  // Handle referral code from URL parameter (UUID)
  useEffect(() => {
    if (!referralParam) return;

    const verifyCode = async () => {
      // 2. Server-side Verification - URL contains UUID, API returns ref code if valid
      setIsValidating(true);
      try {
        const response = await validateRefCode(referralParam);

        if (response && response?.data?.referral_code) {
          const refCode = response?.data?.referral_code.toUpperCase();
          const codeArray = refCode.padEnd(6, "").split("").slice(0, 6);
          setReferralCode(codeArray);
          setFormData((prev) => ({
            ...prev,
            referralCode: refCode,
          }));
          setIsReferralLocked(true);
        } else {
          // API says UUID is not valid
          navigate("/404", { replace: true });
        }
      } catch (error) {
        // If API fails, redirect to 404
        console.error("Referral validation failed", error);
        navigate("/404", { replace: true });
      } finally {
        setIsValidating(false);
      }
    };

    verifyCode();
  }, [referralParam, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If the field is phone, only allow numeric input
    if (name === "phone") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      return;
    }

    // If the field is age, only allow numeric input and max 3 digits (for age up to 100)
    if (name === "age") {
      const numericValue = value.replace(/[^0-9]/g, "");
      // Limit to 3 digits
      const limitedValue = numericValue.slice(0, 3);
      setFormData((prev) => ({
        ...prev,
        [name]: limitedValue,
      }));
      return;
    }

    // If the field is siblings, only allow numeric input
    if (name === "siblings") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Fetch prefilled data when user enters valid email
  const handleEmailValidation = async (email) => {
    if (!email || isEmailLocked) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return;

    // Abort any pending request
    if (emailAbortControllerRef.current) {
      emailAbortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    emailAbortControllerRef.current = new AbortController();
    const signal = emailAbortControllerRef.current.signal;

    setIsEmailValidating(true);

    try {
      const response = await fetchDataByEmail(email, signal);
      const responseData = response?.data?.data;

      if (responseData) {
        const page1 = responseData.page_no_1_data;
        const page2 = responseData.page_no_2_data;
        const page3 = responseData.page_no_3_data;
        console.log("Prefilled data response:", responseData);
        // Check if any page data exists
        const hasData = page1 || page2 || page3;

        if (hasData) {
          // Handle referral code from API (only if not locked from URL)
          if (!isReferralLocked && page1?.referred_by_code) {
            const refCode = page1.referred_by_code.toUpperCase();
            const codeArray = refCode.padEnd(6, "").split("").slice(0, 6);
            setReferralCode(codeArray);
          }

          // Map API response to formData
          setFormData((prev) => ({
            ...prev,
            // Referral code (only if not locked from URL)
            referralCode:
              !isReferralLocked && page1?.referred_by_code
                ? page1.referred_by_code.toUpperCase()
                : prev.referralCode,
            // Page 1 fields
            name: page1?.name || prev.name,
            age: page1?.age?.toString() || prev.age,
            gender: page1?.gender || prev.gender,
            district: page1?.district || prev.district,
            assemblyConstituency:
              page1?.assembly_constituency || prev.assemblyConstituency,
            panchayat: page1?.panchayat || prev.panchayat,
            village: page1?.village || prev.village,
            phone: page1?.phone?.toString() || prev.phone,
            himachaliDomicile:
              page1?.himachal_domicile === true
                ? "Yes"
                : page1?.himachal_domicile === false
                  ? "No"
                  : prev.himachaliDomicile,
            category: page1?.category || prev.category,
            bpl:
              page1?.is_bpl === true
                ? "Yes"
                : page1?.is_bpl === false
                  ? "No"
                  : prev.bpl,
            maritalStatus: page1?.marriatal_status || prev.maritalStatus,
            // Page 2 fields
            educationalQualification:
              page2?.educational_qualification || prev.educationalQualification,
            profession: page2?.profession || prev.profession,
            youthOrganization:
              page2?.worked_with_youth_organization || prev.youthOrganization,
            leadershipProgram:
              page2?.selected_for_youth_program || prev.leadershipProgram,
            motherName: page2?.mother_name || prev.motherName,
            motherProfession: page2?.mother_profession || prev.motherProfession,
            fatherName: page2?.father_name || prev.fatherName,
            fatherProfession: page2?.father_profession || prev.fatherProfession,
            siblings: page2?.no_of_siblings?.toString() || prev.siblings,
            // Page 3 fields
            mountainOrBeach: page3?.mountain_or_beach || prev.mountainOrBeach,
            // Handle favourite podcaster - check if it matches predefined options
            favoritePodcaster: page3?.favourite_podcaster
              ? ["Raj Shamani", "Nikhil Kamath"].includes(
                  page3.favourite_podcaster,
                )
                ? page3.favourite_podcaster
                : "Other"
              : prev.favoritePodcaster,
            favoritePodcasterOther: page3?.favourite_podcaster
              ? ["Raj Shamani", "Nikhil Kamath"].includes(
                  page3.favourite_podcaster,
                )
                ? prev.favoritePodcasterOther
                : page3.favourite_podcaster
              : prev.favoritePodcasterOther,
            binodOrKiris: page3?.binod_or_kirish || prev.binodOrKiris,
            aiPreference: page3?.gemini_gpt_perplexity || prev.aiPreference,
            noobFriends: page3?.no_of_noob_friends || prev.noobFriends,
            aiImagePrompt: page3?.ai_image_prompt || prev.aiImagePrompt,
            ghazalOrHipHop: page3?.ghazal_or_hip_hop || prev.ghazalOrHipHop,
            changemakersWord:
              page3?.thoughts_on_changemakers || prev.changemakersWord,
            issueToSolve: page3?.solve_issue_in_himachal || prev.issueToSolve,
          }));
          setIsEmailLocked(true);
          toast.success("Form data loaded from your previous submission");
        } else {
          // No data found but email is valid - lock it
          setIsEmailLocked(true);
        }
      } else {
        // No data found but email is valid - lock it
        setIsEmailLocked(true);
      }
    } catch (error) {
      // If request was aborted, don't update state
      if (error.name === "AbortError" || error.name === "CanceledError") {
        console.log("Email validation request was cancelled");
        return;
      }

      // Check if user already submitted final form (400 error)
      const errorMessage =
        error.response?.data?.data?.message || error.response?.data?.message;
      if (
        error.response?.status === 400 &&
        errorMessage === "User already submitted final form"
      ) {
        toast.error(
          "You have already submitted your application with this email. Please use a different email or contact support.",
        );
        setIsEmailLocked(false);
        return;
      }

      // No prefilled data - still lock email as it's validated
      setIsEmailLocked(true);
      console.log("No prefilled data found for email:", email);
    } finally {
      setIsEmailValidating(false);
      emailAbortControllerRef.current = null;
    }
  };

  // Handle email blur for all devices
  const handleEmailBlur = (e) => {
    const email = e.target.value?.trim();
    handleEmailValidation(email);
  };

  // Handle edit email button click
  const handleEditEmail = () => {
    // Abort any pending validation request
    if (emailAbortControllerRef.current) {
      emailAbortControllerRef.current.abort();
      emailAbortControllerRef.current = null;
    }
    setIsEmailValidating(false);
    setIsEmailLocked(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Show loading state while validating
      setIsUploading(true);
      setUploadProgress(0);

      // Validate video file (includes extension, MIME, size, and duration checks)
      const validation = await validateVideoFile(file, {
        maxSize: 200 * 1024 * 1024, // 200MB
        maxDuration: 120, // 2 minutes
        checkDuration: true,
      });

      if (!validation.isValid) {
        toast.error(validation.error);
        e.target.value = ""; // Reset file input
        setIsUploading(false);
        return;
      }

      // Validation passed - store file and start upload
      setFormData((prev) => ({
        ...prev,
        videoUpload: file,
      }));

      // Start upload automatically
      startVideoUpload(file);
    }
  };

  // Save page 3 data when user clicks on video upload button
  const handleVideoButtonClick = async () => {
    try {
      await clickNext(3, formData);
      console.log("Page 3 data saved on video button click");
    } catch (error) {
      console.error("Failed to save page 3 data:", error);
      // Don't show error to user - continue with video selection
    }
  };

  const startVideoUpload = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress during upload
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 300);

      // Call the actual upload API
      const response = await uploadVideo(file, formData.email);

      clearInterval(progressInterval);
      setUploadProgress(100);
      setIsUploading(false);

      console.log("Video uploaded successfully:", response);
      console.log("Video path", response?.data?.data?.path);

      // Extract filename from API response
      // Response format: { status: true, message: "...", data: { path: "1769882131589-samplevideo.mp4" } }
      const filename = response.data?.data?.path || response.path || file.name;
      setUploadedVideoFilename(filename);
      console.log("Uploaded video filename:", filename);

      toast.success("Video uploaded successfully!");
    } catch (error) {
      console.error("Video upload error:", error);
      toast.error("Video upload failed. Please try again.");
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancelUpload = () => {
    setIsUploading(false);
    setUploadProgress(0);
    setUploadedVideoFilename(null);
    setFormData((prev) => ({
      ...prev,
      videoUpload: null,
    }));
  };

  const handleReferralCodeChange = (index, value) => {
    // Only allow single alphanumeric character
    const sanitizedValue = value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 1);

    const newReferralCode = [...referralCode];
    newReferralCode[index] = sanitizedValue;
    setReferralCode(newReferralCode);

    // Update formData with complete referral code
    setFormData((prev) => ({
      ...prev,
      referralCode: newReferralCode.join(""),
    }));

    // Auto-focus next input
    if (sanitizedValue && index < 5) {
      const nextInput = document.getElementById(`referral-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleReferralKeyDown = (index, e) => {
    // Handle backspace to go to previous input
    if (e.key === "Backspace" && !referralCode[index] && index > 0) {
      const prevInput = document.getElementById(`referral-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleNext = async () => {
    // Validate fields in order as they appear in the form
    if (step === 1) {
      // 1. Name - check required and minimum 3 characters
      if (!formData.name || formData.name.trim() === "") {
        toast.error("Please fill in the required field: Name");
        const fieldElement = document.querySelector('[name="name"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }
      if (formData.name.trim().length < 3) {
        toast.error("Name must be at least 3 characters long");
        const fieldElement = document.querySelector('[name="name"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 2. Age - check required and validation
      if (!formData.age || formData.age.trim() === "") {
        toast.error("Please fill in the required field: Age");
        const fieldElement = document.querySelector('[name="age"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }
      const age = parseInt(formData.age);
      if (isNaN(age) || age < 16 || age > 100) {
        toast.error("Age must be between 16-100 years to apply");
        const fieldElement = document.querySelector('[name="age"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 3. Gender
      if (!formData.gender || formData.gender.trim() === "") {
        toast.error("Please fill in the required field: Gender");
        const fieldElement = document.querySelector('[name="gender"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 4. District
      if (!formData.district || formData.district.trim() === "") {
        toast.error("Please fill in the required field: District");
        const fieldElement = document.querySelector('[name="district"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 5. Assembly Constituency - check required and minimum 3 characters
      if (
        !formData.assemblyConstituency ||
        formData.assemblyConstituency.trim() === ""
      ) {
        toast.error("Please fill in the required field: Assembly Constituency");
        const fieldElement = document.querySelector(
          '[name="assemblyConstituency"]',
        );
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }
      if (formData.assemblyConstituency.trim().length < 3) {
        toast.error("Assembly Constituency must be at least 3 characters long");
        const fieldElement = document.querySelector(
          '[name="assemblyConstituency"]',
        );
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 6. Panchayat - check required and minimum 3 characters
      if (!formData.panchayat || formData.panchayat.trim() === "") {
        toast.error("Please fill in the required field: Panchayat");
        const fieldElement = document.querySelector('[name="panchayat"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }
      if (formData.panchayat.trim().length < 3) {
        toast.error("Panchayat must be at least 3 characters long");
        const fieldElement = document.querySelector('[name="panchayat"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 7. Village - check required and minimum 3 characters
      if (!formData.village || formData.village.trim() === "") {
        toast.error("Please fill in the required field: Village");
        const fieldElement = document.querySelector('[name="village"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }
      if (formData.village.trim().length < 3) {
        toast.error("Village must be at least 3 characters long");
        const fieldElement = document.querySelector('[name="village"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 8. Phone - check required and validation
      if (!formData.phone || formData.phone.trim() === "") {
        toast.error("Please fill in the required field: Phone Number");
        const fieldElement = document.querySelector('[name="phone"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }
      const phoneLength = formData.phone.length;
      if (phoneLength !== 10 || !/^\d+$/.test(formData.phone)) {
        toast.error("Please enter a valid 10-digit phone number");
        const fieldElement = document.querySelector('[name="phone"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }
      // Indian mobile numbers must start with 7, 8, or 9
      if (!/^[6789]/.test(formData.phone)) {
        toast.error("Indian mobile number must start with 6, 7, 8, or 9");
        const fieldElement = document.querySelector('[name="phone"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 9. Email - check required and validation
      if (!formData.email || formData.email.trim() === "") {
        toast.error("Please fill in the required field: Email");
        const fieldElement = document.querySelector('[name="email"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }
      const email = formData.email.trim();
      // Check for @ symbol first
      if (!email.includes("@")) {
        toast.error("Email must be valid");
        const emailField = document.querySelector('[name="email"]');
        if (emailField) {
          emailField.scrollIntoView({ behavior: "smooth", block: "center" });
          emailField.focus();
        }
        return;
      }
      // Check for domain extension
      if (!email.includes(".") || email.lastIndexOf(".") < email.indexOf("@")) {
        toast.error("Email must have a valid domain (e.g., @example.com)");
        const emailField = document.querySelector('[name="email"]');
        if (emailField) {
          emailField.scrollIntoView({ behavior: "smooth", block: "center" });
          emailField.focus();
        }
        return;
      }
      // Full email validation
      const emailRegex =
        /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        toast.error(
          "Please enter a valid email address (e.g., name@example.com)",
        );
        const emailField = document.querySelector('[name="email"]');
        if (emailField) {
          emailField.scrollIntoView({ behavior: "smooth", block: "center" });
          emailField.focus();
        }
        return;
      }
      // Additional email validation checks
      if (email.includes("..")) {
        toast.error("Email cannot contain consecutive dots");
        const emailField = document.querySelector('[name="email"]');
        if (emailField) {
          emailField.scrollIntoView({ behavior: "smooth", block: "center" });
          emailField.focus();
        }
        return;
      }
      if (email.startsWith(".") || email.endsWith(".")) {
        toast.error("Email cannot start or end with a dot");
        const emailField = document.querySelector('[name="email"]');
        if (emailField) {
          emailField.scrollIntoView({ behavior: "smooth", block: "center" });
          emailField.focus();
        }
        return;
      }

      // 10. Himachali Domicile
      if (
        !formData.himachaliDomicile ||
        formData.himachaliDomicile.trim() === ""
      ) {
        toast.error("Please fill in the required field: Himachali Domicile");
        const fieldElement = document.querySelector(
          '[name="himachaliDomicile"]',
        );
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 11. Category
      if (!formData.category || formData.category.trim() === "") {
        toast.error("Please fill in the required field: Category");
        const fieldElement = document.querySelector('[name="category"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 12. BPL Status
      if (!formData.bpl || formData.bpl.trim() === "") {
        toast.error("Please fill in the required field: BPL Status");
        const fieldElement = document.querySelector('[name="bpl"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }

      // 13. Marital Status
      if (!formData.maritalStatus || formData.maritalStatus.trim() === "") {
        toast.error("Please fill in the required field: Marital Status");
        const fieldElement = document.querySelector('[name="maritalStatus"]');
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          fieldElement.focus();
        }
        return;
      }
    } else if (step === 2) {
      // Validate Step 2 fields in order
      const step2Fields = [
        {
          name: "educationalQualification",
          label: "Educational Qualification",
          minLength: 0,
        },
        { name: "profession", label: "Profession", minLength: 3 },
        {
          name: "youthOrganization",
          label: "Youth Organization Experience",
          minLength: 3,
        },
        {
          name: "leadershipProgram",
          label: "Leadership Program Experience",
          minLength: 3,
        },
        { name: "motherName", label: "Mother's Name", minLength: 3 },
        {
          name: "motherProfession",
          label: "Mother's Profession",
          minLength: 3,
        },
        { name: "fatherName", label: "Father's Name", minLength: 3 },
        {
          name: "fatherProfession",
          label: "Father's Profession",
          minLength: 3,
        },
        { name: "siblings", label: "Siblings Information", minLength: 0 },
      ];

      for (const field of step2Fields) {
        if (!formData[field.name] || formData[field.name].trim() === "") {
          toast.error(`Please fill in the required field: ${field.label}`);
          const fieldElement = document.querySelector(`[name="${field.name}"]`);
          if (fieldElement) {
            fieldElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            fieldElement.focus();
          }
          return;
        }
        if (
          field.minLength > 0 &&
          formData[field.name].trim().length < field.minLength
        ) {
          toast.error(
            `${field.label} must be at least ${field.minLength} characters long`,
          );
          const fieldElement = document.querySelector(`[name="${field.name}"]`);
          if (fieldElement) {
            fieldElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            fieldElement.focus();
          }
          return;
        }
      }
    }

    setLoading(true);

    try {
      // Call click-next API with current page data
      const currentPage = step;
      await clickNext(currentPage, formData);

      // Lock referral code after completing step 1 (cannot be changed once submitted)
      if (currentPage === 1) {
        setIsReferralLocked(true);
      }

      // Move to next step
      setStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error saving page data:", error);

      // Extract error message from API response
      let errorMsg = `Failed to save page ${step} data. Please try again.`;

      // Check various possible error message locations
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.data?.data?.message) {
        // Handle nested data.message structure
        errorMsg = error.response.data.data.message;
      } else if (error.data?.message) {
        errorMsg = error.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }

      toast.error(errorMsg);

      // Scroll to top to make toast visible
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate Step 3 fields in EXACT order as they appear on form (all required except video and referral code)

    // 1. Mountain or Beach preference (Radio Button)
    if (!formData.mountainOrBeach || formData.mountainOrBeach.trim() === "") {
      toast.error("Please select: Are you a mountain person or beach person?");
      const fieldElement = document.querySelector('[name="mountainOrBeach"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }

    // 2. Favourite Podcaster (Radio Button)
    if (
      !formData.favoritePodcaster ||
      formData.favoritePodcaster.trim() === ""
    ) {
      toast.error("Please select: Who is your favourite Podcaster?");
      const fieldElement = document.querySelector('[name="favoritePodcaster"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }

    // 2a. Validate "Other" option for podcaster (Text Field - conditional)
    if (formData.favoritePodcaster === "Other") {
      if (
        !formData.favoritePodcasterOther ||
        formData.favoritePodcasterOther.trim() === ""
      ) {
        toast.error("Please specify your favourite podcaster");
        const podcasterOtherField = document.querySelector(
          '[name="favoritePodcasterOther"]',
        );
        if (podcasterOtherField) {
          podcasterOtherField.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          podcasterOtherField.focus();
        }
        return;
      }
      if (formData.favoritePodcasterOther.trim().length < 3) {
        toast.error(
          "Favourite Podcaster name must be at least 3 characters long",
        );
        const podcasterOtherField = document.querySelector(
          '[name="favoritePodcasterOther"]',
        );
        if (podcasterOtherField) {
          podcasterOtherField.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          podcasterOtherField.focus();
        }
        return;
      }
    }

    // 3. Binod or Kiris question (Radio Button)
    if (!formData.binodOrKiris || formData.binodOrKiris.trim() === "") {
      toast.error("Please select: देख रहा है विनोद या किरिस का गाना सुनेगा");
      const fieldElement = document.querySelector('[name="binodOrKiris"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }

    // 4. AI tool preference (Radio Button)
    if (!formData.aiPreference || formData.aiPreference.trim() === "") {
      toast.error("Please select: Gemini, ChatGPT or Perplexity preference");
      const fieldElement = document.querySelector('[name="aiPreference"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }

    // 5. Number of Noob friends (Radio Button)
    if (!formData.noobFriends || formData.noobFriends.trim() === "") {
      toast.error("Please select: How many Noob friends do you have?");
      const fieldElement = document.querySelector('[name="noobFriends"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }

    // 6. AI image prompt (Text Field) - check required and minimum 3 characters
    if (!formData.aiImagePrompt || formData.aiImagePrompt.trim() === "") {
      toast.error("Please fill in the required field: AI image prompt");
      const fieldElement = document.querySelector('[name="aiImagePrompt"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }
    if (formData.aiImagePrompt.trim().length < 3) {
      toast.error("AI image prompt must be at least 3 characters long");
      const fieldElement = document.querySelector('[name="aiImagePrompt"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }
    // Validate word count for AI image prompt (should be max 60 words)
    const wordCount = formData.aiImagePrompt
      .split(" ")
      .filter((word) => word.length > 0).length;
    if (wordCount > 60) {
      toast.error("AI image prompt must not exceed 60 words");
      const promptField = document.querySelector('[name="aiImagePrompt"]');
      if (promptField) {
        promptField.scrollIntoView({ behavior: "smooth", block: "center" });
        promptField.focus();
      }
      return;
    }

    // 7. Ghazal or Hip Hop preference (Radio Button)
    if (!formData.ghazalOrHipHop || formData.ghazalOrHipHop.trim() === "") {
      toast.error("Please select: Do you vibe on Ghazal or Hip Hop?");
      const fieldElement = document.querySelector('[name="ghazalOrHipHop"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }

    // 8. Changemakers word (Text Field) - check required and minimum 3 characters
    if (!formData.changemakersWord || formData.changemakersWord.trim() === "") {
      toast.error("Please fill in the required field: Changemakers word");
      const fieldElement = document.querySelector('[name="changemakersWord"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }
    if (formData.changemakersWord.trim().length < 3) {
      toast.error("Changemakers word must be at least 3 characters long");
      const fieldElement = document.querySelector('[name="changemakersWord"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }

    // 9. Issue to solve (Text Field) - check required and minimum 3 characters
    if (!formData.issueToSolve || formData.issueToSolve.trim() === "") {
      toast.error("Please fill in the required field: Issue to solve");
      const fieldElement = document.querySelector('[name="issueToSolve"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }
    if (formData.issueToSolve.trim().length < 3) {
      toast.error("Issue to solve must be at least 3 characters long");
      const fieldElement = document.querySelector('[name="issueToSolve"]');
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
      return;
    }

    // 10. Video upload - NOT REQUIRED (optional)
    // 11. Referral Code - NOT REQUIRED (optional)

    // Check if video upload is in progress
    if (formData.videoUpload && uploadProgress < 100 && isUploading) {
      toast.error("Please wait for video upload to complete");
      return;
    }

    // 12. Captcha validation - REQUIRED
    if (!captchaToken) {
      setCaptchaError("Please complete the captcha verification");
      toast.error("Please complete the captcha verification");
      return;
    }

    setLoading(true);

    try {
      let videoFilename = null;

      // Step 1: Upload video if present
      if (
        formData.videoUpload &&
        uploadProgress === 100 &&
        uploadedVideoFilename
      ) {
        // Video already uploaded via resumable upload, use the stored filename
        videoFilename = uploadedVideoFilename;
      } else if (formData.videoUpload && uploadProgress !== 100) {
        // Upload video using regular API if resumable upload wasn't completed
        const videoResponse = await uploadVideo(
          formData.videoUpload,
          formData.email,
        );
        // Extract filename from response: { status: true, data: { path: "1769882131589-samplevideo.mp4" } }
        videoFilename =
          videoResponse.data?.path ||
          videoResponse.path ||
          formData.videoUpload.name;
      }

      // Step 2: Submit complete form with all data (send filename only, not full URL)
      // Include captcha token for server-side verification
      const response = await submitForm(formData, videoFilename, captchaToken);

      console.log("Submission successful:", response);

      // Get the referral code from response
      const myReferralCode =
        response.data?.my_referral_code || response.my_referral_code || "";
      const codeUuid = response.data?.code_uuid || response.code_uuid || "";

      // Show success message
      toast.success("Registration submitted successfully!");

      // Redirect to thank you page with referral code
      setTimeout(() => {
        navigate("/thank-you", {
          state: { referralCode: myReferralCode, codeUuid: codeUuid },
        });
      }, 1000);
    } catch (error) {
      console.error("Submission error:", error);

      // Extract error message from axios error response
      let errorMsg = "Failed to submit registration. Please try again.";
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }

      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const districts = [
    "Hamirpur",
    "Una",
    "Bilaspur",
    "Mandi",
    "Kangra",
    "Chamba",
    "Kinnaur",
    "Kullu",
    "Lahual & Spiti",
    "Shimla",
    "Sirmaur",
    "Solan",
  ];

  const educationLevels = ["11th", "12th", "Graduation", "Post Graduation"];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#FFF9F0", py: 4 }}>
      <Container maxWidth="md">
        {/* White Card Container */}
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            p: { xs: 3, md: 4 },
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
            position: "relative",
          }}
        >
          {/* Show LinearProgress while validating referral code from URL */}
          {isValidating && (
            <LinearProgress
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderRadius: "16px 16px 0 0",
                backgroundColor: "#FFF9F0",
                "& .MuiLinearProgress-bar": { backgroundColor: "#87EA5C" },
              }}
            />
          )}
          {/* Header Image */}
          <Box
            component="img"
            src="/assets/popup.jpg"
            alt="Registration Header"
            sx={{
              width: "100%",
              borderRadius: "16px",
              mb: 4,
            }}
          />

          {/* Form Description */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: "Test Domaine Display, serif",
                fontSize: { xs: "32px", md: "42px" },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Yuva Changemakers
            </Typography>
            <Typography
              sx={{
                fontFamily: "Test Domaine Display, serif",
                fontSize: { xs: "24px", md: "28px" },
                fontWeight: 500,
                mb: 3,
              }}
            >
              Tomorrow's Leaders, Today
            </Typography>
            <Typography
              sx={{
                fontFamily: "Elza, sans-serif",
                fontSize: { xs: "16px", md: "18px" },
                lineHeight: 1.6,
                color: "#333",
              }}
            >
              Come and take part in revolutionizing Himachal Pradesh. Become the
              leaders of future. Take a step towards Hon'ble Prime Minister Shri
              Narendra Modi's initiative to bring 1 lakh young individuals into
              Indian Politics. Present your vision and get access to real life
              community programs and get a chance to win a 1-year Leadership
              fellowship. Fill this Form to start your journey towards building
              a Viksit Himachal Pradesh aligned with the goal of building Viksit
              Bharat by 2047.
            </Typography>
          </Box>

          {/* Referral Code - Always visible, disabled after step 1 */}
          <Box sx={{ mb: 4 }}>
            <FormLabel sx={{ mb: 2, display: "block" }}>
              Referral Code {isReferralLocked ? "(Locked)" : "(Optional)"}
            </FormLabel>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1, md: 2 },
                justifyContent: "center",
              }}
            >
              {referralCode.map((digit, index) => (
                <TextField
                  key={index}
                  id={`referral-${index}`}
                  value={digit}
                  onChange={(e) =>
                    handleReferralCodeChange(index, e.target.value)
                  }
                  onKeyDown={(e) => handleReferralKeyDown(index, e)}
                  disabled={isReferralLocked || step > 1}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: "24px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                    },
                  }}
                  sx={{
                    width: { xs: "45px", md: "60px" },
                    "& .MuiOutlinedInput-root": {
                      height: { xs: "50px", md: "60px" },
                      backgroundColor:
                        isReferralLocked || step > 1 ? "#e0e0e0" : "#f5f5f5",
                      "&:hover": {
                        backgroundColor:
                          isReferralLocked || step > 1 ? "#e0e0e0" : "#ebebeb",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#87EA5C",
                          borderWidth: "2px",
                        },
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#e0e0e0",
                        "& fieldset": {
                          borderColor: "#ccc",
                        },
                      },
                    },
                  }}
                />
              ))}
            </Box>
            {step === 1 && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 1,
                  color: "#666",
                }}
              >
                {isReferralLocked
                  ? "Referral code is locked from URL"
                  : "Enter your 6-character referral code if you have one"}
              </Typography>
            )}
          </Box>

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              "& .MuiInputBase-root": {
                fontFamily: "Elza, sans-serif",
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Elza, sans-serif",
              },
              "& .MuiFormLabel-root": {
                fontFamily: "Elza, sans-serif",
              },
              "& .MuiTypography-root": {
                fontFamily: "Elza, sans-serif",
              },
              "& .MuiButton-root": {
                fontFamily: "Elza, sans-serif",
              },
            }}
          >
            {step === 1 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "Elza, sans-serif",
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Personal Information
                </Typography>
                <Box>
                  <TextField
                    required
                    fullWidth
                    label="Email ID"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleEmailBlur}
                    disabled={isEmailLocked || isEmailValidating}
                    helperText={
                      isEmailValidating
                        ? "Validating your email, checking if application already exists..."
                        : isEmailLocked
                          ? "Email verified"
                          : "Enter a valid email (e.g., name@example.com)"
                    }
                    slotProps={{
                      input: {
                        endAdornment: isEmailValidating ? (
                          <CircularProgress size={20} />
                        ) : isEmailLocked ? (
                          <Button
                            size="small"
                            onClick={handleEditEmail}
                            sx={{
                              minWidth: "auto",
                              fontSize: "12px",
                              textTransform: "none",
                            }}
                          >
                            Edit
                          </Button>
                        ) : null,
                      },
                    }}
                  />
                </Box>
                <TextField
                  required
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />

                <TextField
                  required
                  fullWidth
                  label="Age"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  slotProps={{
                    htmlInput: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 3,
                    },
                  }}
                  helperText="Enter age between 16-29 years"
                />

                <FormControl required>
                  <FormLabel>Gender</FormLabel>
                  <RadioGroup
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Male"
                      control={<Radio />}
                      label="Male"
                    />
                    <FormControlLabel
                      value="Female"
                      control={<Radio />}
                      label="Female"
                    />
                    <FormControlLabel
                      value="Third Gender"
                      control={<Radio />}
                      label="Third Gender"
                    />
                  </RadioGroup>
                </FormControl>

                <FormControl required fullWidth>
                  <InputLabel>District</InputLabel>
                  <Select
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    label="District"
                  >
                    {districts.map((district) => (
                      <MenuItem key={district} value={district}>
                        {district}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  required
                  fullWidth
                  label="Assembly Constituency"
                  name="assemblyConstituency"
                  value={formData.assemblyConstituency}
                  onChange={handleChange}
                />

                <TextField
                  required
                  fullWidth
                  label="Panchayat"
                  name="panchayat"
                  value={formData.panchayat}
                  onChange={handleChange}
                />

                <TextField
                  required
                  fullWidth
                  label="Village"
                  name="village"
                  value={formData.village}
                  onChange={handleChange}
                />

                <TextField
                  required
                  fullWidth
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  slotProps={{
                    htmlInput: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 10,
                    },
                  }}
                />

                <FormControl required>
                  <FormLabel>Do you have a Himachali Domicile?</FormLabel>
                  <RadioGroup
                    name="himachaliDomicile"
                    value={formData.himachaliDomicile}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>

                <FormControl required fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    label="Category"
                  >
                    <MenuItem value="General(UR)">General(UR)</MenuItem>
                    <MenuItem value="OBC">OBC</MenuItem>
                    <MenuItem value="SC">SC</MenuItem>
                    <MenuItem value="ST">ST</MenuItem>
                  </Select>
                </FormControl>

                <FormControl required>
                  <FormLabel>Whether BPL or Not?</FormLabel>
                  <RadioGroup
                    name="bpl"
                    value={formData.bpl}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>

                <FormControl required>
                  <FormLabel>Married/Unmarried</FormLabel>
                  <RadioGroup
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Married"
                      control={<Radio />}
                      label="Married"
                    />
                    <FormControlLabel
                      value="Unmarried"
                      control={<Radio />}
                      label="Unmarried"
                    />
                  </RadioGroup>
                </FormControl>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleNext}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#87EA5C",
                    color: "#000",
                    fontFamily: "Elza, sans-serif",
                    fontSize: "18px",
                    fontWeight: 600,
                    py: 1.5,
                    mt: 2,
                    "&:hover": {
                      backgroundColor: "#75d84a",
                    },
                    "&:disabled": {
                      backgroundColor: "#cccccc",
                    },
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{ mr: 1, color: "#000" }}
                      />
                      Saving...
                    </>
                  ) : (
                    "Next"
                  )}
                </Button>
              </Box>
            )}

            {step === 2 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "Elza, sans-serif",
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Additional Information
                </Typography>

                <FormControl required fullWidth>
                  <InputLabel>Educational Qualification</InputLabel>
                  <Select
                    name="educationalQualification"
                    value={formData.educationalQualification}
                    onChange={handleChange}
                    label="Educational Qualification"
                  >
                    {educationLevels.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  required
                  fullWidth
                  label="Profession"
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                />
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Have you worked with any Youth Organization (Chhatra
                  Sangathan)? If yes, kindly mention here.
                </Typography>

                <TextField
                  required
                  fullWidth
                  multiline
                  rows={3}
                  name="youthOrganization"
                  value={formData.youthOrganization}
                  onChange={handleChange}
                  placeholder="You can type here"
                />
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Have you ever applied or been selected for any youth
                  leadership program? If yes, kindly mention here.
                </Typography>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="You can type here"
                  name="leadershipProgram"
                  value={formData.leadershipProgram}
                  onChange={handleChange}
                />

                <TextField
                  required
                  fullWidth
                  label="Mother's Name"
                  name="motherName"
                  value={formData.motherName}
                  onChange={handleChange}
                />

                <TextField
                  required
                  fullWidth
                  label="Mother's Profession"
                  name="motherProfession"
                  value={formData.motherProfession}
                  onChange={handleChange}
                />

                <TextField
                  required
                  fullWidth
                  label="Father's Name"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleChange}
                />

                <TextField
                  required
                  fullWidth
                  label="Father's Profession"
                  name="fatherProfession"
                  value={formData.fatherProfession}
                  onChange={handleChange}
                />
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Do you have Siblings? If Yes, How many?
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="siblings"
                  value={formData.siblings}
                  placeholder="You can type here"
                  onChange={handleChange}
                  slotProps={{
                    htmlInput: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    },
                  }}
                  helperText="Enter number (e.g., 0, 1, 2, 3...)"
                />

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleNext}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#87EA5C",
                    color: "#000",
                    fontFamily: "Elza, sans-serif",
                    fontSize: "18px",
                    fontWeight: 600,
                    py: 1.5,
                    mt: 2,
                    "&:hover": {
                      backgroundColor: "#75d84a",
                    },
                    "&:disabled": {
                      backgroundColor: "#cccccc",
                    },
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{ mr: 1, color: "#000" }}
                      />
                      Saving...
                    </>
                  ) : (
                    "Next"
                  )}
                </Button>
              </Box>
            )}

            {step === 3 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "Elza, sans-serif",
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Fun Questions & Your Vision
                </Typography>

                <FormControl required>
                  <FormLabel>
                    Are you a mountain person or beach person?
                  </FormLabel>
                  <RadioGroup
                    name="mountainOrBeach"
                    value={formData.mountainOrBeach}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Mountain"
                      control={<Radio />}
                      label="Mountain"
                    />
                    <FormControlLabel
                      value="Beach"
                      control={<Radio />}
                      label="Beach"
                    />
                  </RadioGroup>
                </FormControl>

                <FormControl required>
                  <FormLabel>Who is your favourite Podcaster?</FormLabel>
                  <RadioGroup
                    name="favoritePodcaster"
                    value={formData.favoritePodcaster}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Raj Shamani"
                      control={<Radio />}
                      label="Raj Shamani"
                    />
                    <FormControlLabel
                      value="Nikhil Kamath"
                      control={<Radio />}
                      label="Nikhil Kamath"
                    />
                    <FormControlLabel
                      value="Other"
                      control={<Radio />}
                      label={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <span>Other:</span>
                          {formData.favoritePodcaster === "Other" && (
                            <TextField
                              size="small"
                              name="favoritePodcasterOther"
                              value={formData.favoritePodcasterOther}
                              onChange={handleChange}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                <FormControl required>
                  <FormLabel>
                    देख रहा है विनोद या किरिस का गाना सुनेगा, Which one is
                    funnier?
                  </FormLabel>
                  <RadioGroup
                    name="binodOrKiris"
                    value={formData.binodOrKiris}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Binod"
                      control={<Radio />}
                      label="Binod"
                    />
                    <FormControlLabel
                      value="Kiris Ka Gaana Sunega"
                      control={<Radio />}
                      label="Kiris Ka Gaana Sunega"
                    />
                  </RadioGroup>
                </FormControl>

                <FormControl required>
                  <FormLabel>
                    Gemini, ChatGPT and Perplexity, Which one is better?
                  </FormLabel>
                  <RadioGroup
                    name="aiPreference"
                    value={formData.aiPreference}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Gemini"
                      control={<Radio />}
                      label="Gemini"
                    />
                    <FormControlLabel
                      value="ChatGPT"
                      control={<Radio />}
                      label="ChatGPT"
                    />
                    <FormControlLabel
                      value="Perplexity"
                      control={<Radio />}
                      label="Perplexity"
                    />
                  </RadioGroup>
                </FormControl>

                <FormControl required>
                  <FormLabel>How many Noob friends do you have?</FormLabel>
                  <RadioGroup
                    name="noobFriends"
                    value={formData.noobFriends}
                    onChange={handleChange}
                  >
                    <FormControlLabel value="1" control={<Radio />} label="1" />
                    <FormControlLabel value="2" control={<Radio />} label="2" />
                    <FormControlLabel
                      value="More than 2"
                      control={<Radio />}
                      label="More than 2"
                    />
                  </RadioGroup>
                </FormControl>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  If you've to make your AI image, write a 60 word prompt (Word
                  Limit 60 Words)
                </Typography>

                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  name="aiImagePrompt"
                  value={formData.aiImagePrompt}
                  placeholder="You can type here"
                  onChange={handleChange}
                  helperText={`${formData.aiImagePrompt.split(" ").filter((word) => word.length > 0).length}/60 words`}
                />

                <FormControl required>
                  <FormLabel>Do You vibe on Ghazal or Hip Hop?</FormLabel>
                  <RadioGroup
                    name="ghazalOrHipHop"
                    value={formData.ghazalOrHipHop}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Ghazal"
                      control={<Radio />}
                      label="Ghazal"
                    />
                    <FormControlLabel
                      value="Hip Hop"
                      control={<Radio />}
                      label="Hip Hop"
                    />
                  </RadioGroup>
                </FormControl>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  First thing that comes to your mind when you hear the word -
                  CHANGEMAKERS. Please give One-Word Answer
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="changemakersWord"
                  value={formData.changemakersWord}
                  placeholder="You can type here"
                  onChange={handleChange}
                />
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  One issue you'd like to solve for Himachal - 250 Words/ 2 Min
                  Video
                </Typography>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={6}
                  name="issueToSolve"
                  value={formData.issueToSolve}
                  onChange={handleChange}
                  helperText="You can write up to 250 words or upload a 2-minute video below"
                  placeholder="You can type here"
                />

                <Box id="video-upload-section">
                  <FormLabel>
                    Upload your Video (Max Limit - 2 minutes)
                  </FormLabel>

                  {!formData.videoUpload && (
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{ mt: 1, display: "block" }}
                      name="videoUpload"
                      onClick={handleVideoButtonClick}
                    >
                      Choose Video
                      <input
                        type="file"
                        hidden
                        name="videoUpload"
                        accept=".mp4,.webm,.mov,.avi,.mkv,.m4v,.3gp,.mpeg,.mpg"
                        onChange={handleFileChange}
                      />
                    </Button>
                  )}

                  {formData.videoUpload && (
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {formData.videoUpload.name}
                        </Typography>

                        <Button
                          size="small"
                          onClick={handleCancelUpload}
                          disabled={isUploading}
                          sx={{
                            color: "#d32f2f",
                            fontSize: "12px",
                            minWidth: "auto",
                          }}
                        >
                          Remove
                        </Button>
                      </Box>

                      {isUploading && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={uploadProgress}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: "#e0e0e0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "#87EA5C",
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              mt: 0.5,
                              textAlign: "center",
                            }}
                          >
                            Uploading: {uploadProgress.toFixed(0)}%
                          </Typography>
                        </Box>
                      )}

                      {uploadProgress === 100 && !isUploading && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 1,
                            color: "#4caf50",
                            fontWeight: 600,
                            textAlign: "center",
                          }}
                        >
                          ✓ Video uploaded successfully!
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                {/* ReCaptcha */}
                <ReCaptcha
                  onVerify={(token) => {
                    setCaptchaToken(token);
                    setCaptchaError("");
                  }}
                  onExpire={() => {
                    setCaptchaToken(null);
                    setCaptchaError("Captcha expired. Please verify again.");
                  }}
                  onError={() => {
                    setCaptchaToken(null);
                    setCaptchaError("Captcha error. Please try again.");
                  }}
                  error={captchaError}
                />

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleBack}
                    sx={{
                      flex: 1,
                      fontFamily: "Elza, sans-serif",
                      fontSize: "18px",
                      py: 1.5,
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      flex: 1,
                      backgroundColor: "#87EA5C",
                      color: "#000",
                      fontFamily: "Elza, sans-serif",
                      fontSize: "18px",
                      fontWeight: 600,
                      py: 1.5,
                      "&:hover": {
                        backgroundColor: "#75d84a",
                      },
                      "&:disabled": {
                        backgroundColor: "#cccccc",
                      },
                    }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress
                          size={20}
                          sx={{ mr: 1, color: "#000" }}
                        />
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      {/* Full screen loading overlay for email validation */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: "column",
          gap: 2,
        }}
        open={isEmailValidating}
      >
        <CircularProgress color="inherit" size={50} />
        <Typography variant="h6" sx={{ fontFamily: "Elza, sans-serif" }}>
          Validating your email...
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontFamily: "Elza, sans-serif", opacity: 0.8 }}
        >
          Checking if application already exists
        </Typography>
      </Backdrop>

      {/* Toast Container for notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
};

export default RegisterPage;
